export const fallbackFontFamily = '-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';
/*
  Patron font family - please reference https://assets.choco.com/fonts/Patron/font.css

  Our previous solution was to have "Patron Medium" and "Patron Light" font families,
  that by default have the styling related to their font-weight

  In case the fonts will fail to load, we have a fallback font family list of fonts to show

  THE PROBLEM WE HAD:
  As the fonts have the styling related to their font-weight, when we fail to load them and
  we fallback this fallback will never be bold because the typography doesn't set a font-weight

  THE SOLUTION WE FOUND:
  We have changed in the font.css file the definition of the font face by calling
  all the different font families with the name "Patron" and specifying the different styling
  for them so we know what to reference

  REAL EXAMPLE:

  @font-face {
    src: url("PatronWEB-Medium.woff?v=01") format("woff"),
      url("PatronWEB-Medium.woff2?v=01") format("woff2");
    
    font-family: "Patron";
    font-weight: 700;
  }

  @font-face {
    src: url("PatronWEB-Light.woff?v=01") format("woff"),
    url("PatronWEB-Light.woff2?v=01") format("woff2");
    
    font-family: "Patron";
    font-weight: 400;
  }

  now, whenever i want to reference "Patron Medium" i just need to specify
  
  `font-family: "Patron" AND font-weight: 700 (same as in the font.css)`
*/
/*
 variable '--line-height' used to allow skeleton text to have the same height as the line-height of the variant
*/
export const text = {
    default: {
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        letterSpacing: 'inherit',
        textTransform: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        '--line-height': 'inherit',
    },
    H1: {
        fontFamily: 'TriadeChoco Backslant',
        fontSize: '72px',
        letterSpacing: '-1.5px',
        textTransform: 'uppercase',
        lineHeight: 'var(--line-height)',
        '--line-height': '72px',
    },
    H2: {
        fontFamily: 'TriadeChoco Backslant',
        fontSize: '60px',
        letterSpacing: '-0.5px',
        textTransform: 'uppercase',
        lineHeight: 'var(--line-height)',
        '--line-height': '60px',
    },
    H3: {
        fontFamily: 'TriadeChoco Upright',
        fontSize: '48px',
        textTransform: 'uppercase',
        lineHeight: 'var(--line-height)',
        '--line-height': '48px',
    },
    H4: {
        fontFamily: 'TriadeChoco Upright',
        fontSize: '36px',
        letterSpacing: '0.25px',
        textTransform: 'uppercase',
        lineHeight: 'var(--line-height)',
        '--line-height': '36px',
    },
    H5: {
        fontFamily: 'TriadeChoco Upright',
        fontSize: '24px',
        letterSpacing: '0.25px',
        textTransform: 'uppercase',
        lineHeight: 'var(--line-height)',
        '--line-height': '24px',
    },
    TitleLarge: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '24px',
        fontWeight: 700,
        lineHeight: 'var(--line-height)',
        '--line-height': '32px',
    },
    TitleMedium: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '21px',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '28px',
    },
    TitleSmall: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '18px',
        lineHeight: 'var(--line-height)',
        fontWeight: 700,
        '--line-height': '22px',
    },
    BodyStrong: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 'var(--line-height)',
        '--line-height': '22px',
    },
    Body: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '22px',
    },
    Subhead: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: 'var(--line-height)',
        '--line-height': '20px',
    },
    Text: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '20px',
    },
    Footnote: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '18px',
    },
    Caption: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '18px',
    },
    Button: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '14px',
        letterSpacing: '1.25px',
        textTransform: 'uppercase',
        fontWeight: 700,
        lineHeight: 'var(--line-height)',
        '--line-height': '24px',
    },
    Overline: {
        fontFamily: `Patron,${fallbackFontFamily}`,
        fontSize: '10px',
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        fontWeight: 400,
        lineHeight: 'var(--line-height)',
        '--line-height': '16px',
    },
    CybersourceInput: {
        fontFamily: fallbackFontFamily,
        fontSize: '16px',
        lineHeight: 'var(--line-height)',
        '--line-height': '22px',
    },
};
//# sourceMappingURL=data:application/json;base64,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